import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import React from 'react';
import TalentTreeFull, {
  TalentTreeFullProps,
} from '@resources/js/components/TalentTreeFull/TalentTreeFull';
import useTalentTreeBlueprint from '@resources/js/contexts/useTalentTreeBlueprint';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';

type BuildsHeroTalentsViewModel = {
  title: string;
  description: string;
  talentTree: TalentTreeFullProps | null;
  subTreeStats: Array<{
    id: number;
    rank: number;
    metricValue: string;
  }>;
};

export default function BuildsHeroTalentsSection({
  title,
  description,
  talentTree,
  subTreeStats,
}: BuildsHeroTalentsViewModel): JSX.Element | null {
  const talentTreeBlueprint = useTalentTreeBlueprint(
    talentTree?.dehydratedBuild?.changeSet
  );
  const talentTrees =
    talentTree &&
    talentTreeBlueprint?.heroTrees
      ?.map((heroTree) => ({
        ...talentTree,
        dehydratedBuild: {
          ...talentTree.dehydratedBuild,
          heroSpecId: heroTree.id,
        },
      }))
      .filter((x) =>
        subTreeStats.some((y) => y.id === x.dehydratedBuild.heroSpecId)
      )
      .sort(
        (x, y) =>
          subTreeStats.findIndex(
            (stat) => stat.id === x.dehydratedBuild.heroSpecId
          ) -
          subTreeStats.findIndex(
            (stat) => stat.id === y.dehydratedBuild.heroSpecId
          )
      );

  const HeroTitle = ({
    treeId,
    treeName,
  }: {
    treeId?: number;
    treeName: string;
  }) => {
    const stats = subTreeStats.find((stat) => stat.id === treeId);
    if (!stats) {
      return <>{treeName}</>;
    }

    if (subTreeStats.length === 1) {
      return (
        <>
          {treeName}:{' '}
          <EnhancedMarkdown markdown={stats.metricValue} wrapped={false} />
        </>
      );
    }

    return (
      <>
        {stats.rank}. {treeName}:{' '}
        <EnhancedMarkdown markdown={stats.metricValue} wrapped={false} />
      </>
    );
  };

  return (
    <VerticalContent className={'builds-hero-talents-section'}>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {subTreeStats.length > 0 && (
        <div className={'builds-hero-talents-section__talent-trees'}>
          {talentTrees &&
            talentTrees.map((tree) => (
              <TalentTreeFull
                key={tree.dehydratedBuild.heroSpecId}
                {...tree}
                defaultExpanded
                hasToggleButton={false}
                exportCodeParams={null}
                heroTreeOnly={true}
                HeroTitle={HeroTitle}
              />
            ))}
        </div>
      )}
      {subTreeStats.length === 0 && <BuildsNoDataPlaceholder />}
    </VerticalContent>
  );
}
